/**
 * Dependency Graph Panel Component
 * Visualise les dépendances et les flux de données du classeur
 * Rendu SVG interactif du graphe
 */

import React, { useState, useMemo } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { DependencyGraph, GraphNode, GraphEdge } from '@excel-ai-studio/shared';
import { callAnalyzeAPI } from '../../services/workbook-brain-api.service';

interface DependencyGraphPanelProps {
  onAnalysisComplete?: (graph: DependencyGraph) => void;
  initialGraph?: DependencyGraph | null;
}

// --- Layout helpers for SVG graph ---

interface LayoutNode {
  node: GraphNode;
  x: number;
  y: number;
  w: number;
  h: number;
}

function computeLayout(nodes: GraphNode[], edges: GraphEdge[], width: number, height: number): LayoutNode[] {
  if (nodes.length === 0) return [];

  // Build adjacency for topological layering
  const incoming = new Map<string, number>();
  const children = new Map<string, string[]>();
  for (const n of nodes) {
    incoming.set(n.id, 0);
    children.set(n.id, []);
  }
  for (const e of edges) {
    incoming.set(e.target, (incoming.get(e.target) ?? 0) + 1);
    children.get(e.source)?.push(e.target);
  }

  // Assign layers via BFS (Kahn's)
  const layers: string[][] = [];
  let queue = nodes.filter((n) => (incoming.get(n.id) ?? 0) === 0).map((n) => n.id);
  const visited = new Set<string>();
  while (queue.length > 0) {
    layers.push([...queue]);
    queue.forEach((id) => visited.add(id));
    const next: string[] = [];
    for (const id of queue) {
      for (const child of children.get(id) ?? []) {
        incoming.set(child, (incoming.get(child) ?? 1) - 1);
        if ((incoming.get(child) ?? 0) <= 0 && !visited.has(child)) {
          next.push(child);
          visited.add(child);
        }
      }
    }
    queue = next;
  }
  // Add any unvisited nodes (cycles) to the last layer
  const remaining = nodes.filter((n) => !visited.has(n.id)).map((n) => n.id);
  if (remaining.length > 0) layers.push(remaining);

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const nodeW = 110;
  const nodeH = 44;
  const result: LayoutNode[] = [];

  const layerCount = layers.length || 1;
  const yStep = (height - nodeH - 20) / Math.max(layerCount - 1, 1);

  for (let li = 0; li < layers.length; li++) {
    const layer = layers[li];
    const xStep = (width - nodeW) / Math.max(layer.length - 1, 1);
    const xOffset = layer.length === 1 ? (width - nodeW) / 2 : 0;
    for (let ni = 0; ni < layer.length; ni++) {
      const n = nodeMap.get(layer[ni]);
      if (!n) continue;
      result.push({
        node: n,
        x: xOffset + ni * (layer.length > 1 ? xStep : 0),
        y: 10 + li * yStep,
        w: nodeW,
        h: nodeH,
      });
    }
  }
  return result;
}

const NODE_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  high: '#f57c00',
  medium: '#fbc02d',
  low: '#388e3c',
};

function nodeColor(importance: string): string {
  return NODE_COLORS[importance] ?? '#1976d2';
}

function strengthLabel(s: string): string {
  return s === 'strong' ? 'fort' : s === 'medium' ? 'moyen' : s === 'weak' ? 'faible' : s;
}

function typeLabel(t: string): string {
  return t === 'formula' ? 'formule' : t === 'reference' ? 'référence' : t;
}

// --- Copy to clipboard helper ---
function copyToClipboard(text: string): void {
  navigator.clipboard.writeText(text).catch(() => {
    /* ignore */
  });
}

export const DependencyGraphPanel: React.FC<DependencyGraphPanelProps> = ({
  onAnalysisComplete,
  initialGraph = null,
}) => {
  const [graph, setGraph] = useState<DependencyGraph | null>(initialGraph);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'graph' | 'flows' | 'risks'>('graph');
  const [copied, setCopied] = useState(false);

  const SVG_W = 480;
  const SVG_H = Math.max(300, (graph?.nodes.length ?? 0) * 60);

  const layout = useMemo(
    () => (graph ? computeLayout(graph.nodes, graph.edges, SVG_W, SVG_H) : []),
    [graph, SVG_W, SVG_H],
  );
  const layoutMap = useMemo(() => new Map(layout.map((l) => [l.node.id, l])), [layout]);

  const analyzeDependencies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await callAnalyzeAPI({ includeScanning: true, includeDependencies: true });
      if (!result.dependencyGraph) throw new Error('Aucun graphe de dépendances retourné par le serveur');
      setGraph(result.dependencyGraph);
      onAnalysisComplete?.(result.dependencyGraph);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'analyser les dépendances");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (!graph) return;
    const lines: string[] = ['=== Graphe de dépendances ===', ''];
    lines.push('Noeuds:');
    for (const n of graph.nodes) {
      lines.push(`  ${n.label} [${n.importance}] in:${n.incomingDependencies} out:${n.outgoingDependencies}`);
    }
    lines.push('', 'Liaisons:');
    for (const e of graph.edges) {
      lines.push(`  ${e.source} -> ${e.target} (${typeLabel(e.type)}, ${strengthLabel(e.strength)})`);
    }
    if (graph.brokenDependencies.length > 0) {
      lines.push('', 'Dépendances brisées:');
      graph.brokenDependencies.forEach((d) => lines.push(`  - ${d}`));
    }
    if (graph.circularRiskAreas.length > 0) {
      lines.push('', 'Risques circulaires:');
      graph.circularRiskAreas.forEach((c) => lines.push(`  - ${c}`));
    }
    copyToClipboard(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Selected node details
  const selectedDetails = useMemo(() => {
    if (!graph || !selectedNode) return null;
    const node = graph.nodes.find((n) => n.id === selectedNode);
    if (!node) return null;
    const inEdges = graph.edges.filter((e) => e.target === selectedNode);
    const outEdges = graph.edges.filter((e) => e.source === selectedNode);
    return { node, inEdges, outEdges };
  }, [graph, selectedNode]);

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingSpinner label="Analyse des dépendances en cours..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Graphe de dépendances</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {graph && (
            <button
              onClick={handleExport}
              style={{ padding: '8px 12px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
            >
              {copied ? 'Copié !' : 'Copier'}
            </button>
          )}
          <button
            onClick={analyzeDependencies}
            style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
          >
            Analyser
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
        {([
          { id: 'graph' as const, label: 'Graphe' },
          { id: 'flows' as const, label: 'Flux de données' },
          { id: 'risks' as const, label: 'Risques' },
        ]).map((mode) => (
          <button
            key={mode.id}
            onClick={() => setViewMode(mode.id)}
            style={{
              padding: '8px 12px',
              backgroundColor: viewMode === mode.id ? '#2196F3' : 'transparent',
              color: viewMode === mode.id ? 'white' : '#666',
              border: 'none', borderRadius: '4px', cursor: 'pointer',
              fontSize: '13px', fontWeight: viewMode === mode.id ? 'bold' : 'normal',
            }}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* SVG Graph View */}
      {graph && viewMode === 'graph' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fafafa', overflow: 'auto' }}>
            <svg width={SVG_W} height={SVG_H} style={{ display: 'block' }}>
              <defs>
                <marker id="arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#999" />
                </marker>
              </defs>

              {/* Edges */}
              {graph.edges.map((edge, idx) => {
                const src = layoutMap.get(edge.source);
                const tgt = layoutMap.get(edge.target);
                if (!src || !tgt) return null;
                const x1 = src.x + src.w / 2;
                const y1 = src.y + src.h;
                const x2 = tgt.x + tgt.w / 2;
                const y2 = tgt.y;
                const strokeW = edge.strength === 'strong' ? 2.5 : edge.strength === 'medium' ? 1.5 : 1;
                const dash = edge.strength === 'weak' ? '4,3' : undefined;
                return (
                  <line
                    key={`e-${idx}`}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="#999" strokeWidth={strokeW}
                    strokeDasharray={dash} markerEnd="url(#arrow)"
                  />
                );
              })}

              {/* Nodes */}
              {layout.map((ln) => {
                const isSelected = selectedNode === ln.node.id;
                return (
                  <g
                    key={ln.node.id}
                    onClick={() => setSelectedNode(isSelected ? null : ln.node.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={ln.x} y={ln.y} width={ln.w} height={ln.h} rx={6}
                      fill={nodeColor(ln.node.importance)}
                      stroke={isSelected ? '#000' : 'none'} strokeWidth={isSelected ? 2 : 0}
                    />
                    <text
                      x={ln.x + ln.w / 2} y={ln.y + 18}
                      textAnchor="middle" fill="white" fontSize={11} fontWeight="bold"
                    >
                      {ln.node.label.length > 14 ? ln.node.label.slice(0, 13) + '...' : ln.node.label}
                    </text>
                    <text
                      x={ln.x + ln.w / 2} y={ln.y + 34}
                      textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize={9}
                    >
                      in:{ln.node.incomingDependencies} out:{ln.node.outgoingDependencies}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Selected node details */}
          {selectedDetails && (
            <div style={{ padding: 12, backgroundColor: '#e3f2fd', borderRadius: 4, fontSize: 12 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{selectedDetails.node.label}</div>
              <div style={{ color: '#555' }}>Type: {selectedDetails.node.type} | Importance: {selectedDetails.node.importance}</div>
              {selectedDetails.inEdges.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <span style={{ fontWeight: 'bold' }}>Entrant:</span>{' '}
                  {selectedDetails.inEdges.map((e) => e.source).join(', ')}
                </div>
              )}
              {selectedDetails.outEdges.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontWeight: 'bold' }}>Sortant:</span>{' '}
                  {selectedDetails.outEdges.map((e) => e.target).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            <div style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px' }}>
              <div style={{ fontWeight: 'bold', color: '#1976d2' }}>Feuilles centrales</div>
              <div style={{ marginTop: '4px', color: '#666' }}>
                {graph.centralSheets.length > 0 ? graph.centralSheets.join(', ') : 'Aucune'}
              </div>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#f3e5f5', borderRadius: '4px', fontSize: '13px' }}>
              <div style={{ fontWeight: 'bold', color: '#7b1fa2' }}>Feuilles terminales</div>
              <div style={{ marginTop: '4px', color: '#666' }}>
                {graph.leafSheets.length > 0 ? graph.leafSheets.join(', ') : 'Aucune'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Flows View */}
      {graph && viewMode === 'flows' && (
        <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '16px', backgroundColor: '#f9f9f9' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '12px', color: '#666' }}>
            Chemins de flux de données ({graph.dataFlowPaths.length})
          </div>
          {graph.dataFlowPaths.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {graph.dataFlowPaths.map((path: string[], idx: number) => (
                <div key={idx} style={{ padding: '8px', backgroundColor: 'white', borderRadius: '4px', fontSize: '12px', border: '1px solid #e0e0e0' }}>
                  {path.map((sheet: string, i: number) => (
                    <span key={i}>
                      <span style={{ color: '#1976d2', fontWeight: 'bold' }}>{sheet}</span>
                      {i < path.length - 1 && <span style={{ margin: '0 4px' }}> &rarr; </span>}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#999', fontSize: '12px' }}>Aucun chemin de flux de données détecté</div>
          )}
        </div>
      )}

      {/* Risks View */}
      {graph && viewMode === 'risks' && (
        <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '16px', backgroundColor: '#f9f9f9' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '12px', color: '#666' }}>
            Risques de dépendances
          </div>
          {graph.brokenDependencies.length > 0 || graph.circularRiskAreas.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {graph.brokenDependencies.map((dep: string, idx: number) => (
                <div key={`broken-${idx}`} style={{ padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '12px', color: '#c62828', border: '1px solid #ef5350' }}>
                  Brisée : {dep}
                </div>
              ))}
              {graph.circularRiskAreas.map((circ: string, idx: number) => (
                <div key={`circular-${idx}`} style={{ padding: '8px', backgroundColor: '#fff3e0', borderRadius: '4px', fontSize: '12px', color: '#e65100', border: '1px solid #ffb74d' }}>
                  Circulaire : {circ}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#2e7d32', fontSize: '12px' }}>Aucun risque de dépendance détecté</div>
          )}
        </div>
      )}
    </div>
  );
};
